import {
  Availability,
  Communication,
  Discount,
  Salon,
  SalonPaymentTypes,
  Stylist,
  Tax,
  Payment
} from '../models';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { List, Map } from 'immutable';

import { ENV_CONFIG } from '../../bin/env.config';
import { ErrorHandlerService } from './error-handler.service';
import { CalendarAvailabilities } from '../bookings/calendar/day-view-calendar/day-view-calendar.component';
import { Injectable } from '@angular/core';
import { Observable, of, firstValueFrom } from 'rxjs';
import {
  HttpReadOptions,
  SalonmonsterHttpClient,
} from '../services/salonmonster-http-client';
import { UserService } from '../services/user.service';
import { Utilities } from '../utilities/utilities';
import { map, tap } from 'rxjs/operators';

/** The card on file settings, as `PUT /salons/card-on-file` takes and returns them. */
export interface CardOnFileSettings {
  requireCardOnFile: boolean;
  noShowPercentage: number;
}

/**
 * Bulk campaign channels. These are the URL segments the report endpoints are
 * keyed by — `text`, not `sms`, is what the API answers to.
 */
export type CampaignChannel = 'email' | 'text';

/**
 * Recipient outcome buckets the server filters by (KTD-14 allowlist). The two
 * channels take different subsets: email filters on the engagement axis, text
 * only on delivery — there is no open tracking on SMS.
 */
export type CampaignRecipientFilter =
  | 'all'
  | 'bounced'
  | 'notdelivered'
  | 'didntopen'
  | 'clicked'
  | 'failed'
  | 'unconfirmed';

export type CampaignRecipientSort = 'problems' | 'name' | 'recent';

/**
 * The server's monotone outcome ladder (R20), computed in SQL so the rows and
 * the filter counts tell one story. NULL means the campaign predates tracking.
 * `optedOut` is text-only, and is a client-level state — not a message status.
 */
export type CampaignRecipientOutcome =
  | 'bounced'
  | 'spam'
  | 'failed'
  | 'optedOut'
  | 'clicked'
  | 'opened'
  | 'delivered'
  | 'sent'
  | 'queued';

export interface CampaignReportTotals {
  sent: number;
  delivered: number;
  /** Email-only engagement axis; zero and meaningless for text. */
  uniqueOpened: number;
  uniqueClicked: number;
  bounced: number;
  spam: number;
  failed: number;
  notConfirmed: number;
  /** NULL until per-campaign unsubscribe attribution ships (R32). */
  unsubscribed: number | null;
}

export interface CampaignReport {
  title: string;
  /**
   * Salon-local on both channels — render it as-is, never convert (KTD-11).
   *
   * DISPLAY ONLY. It carries no timezone designator, so parsing it yields a
   * device-local instant that is wrong by the salon's UTC offset. For any
   * arithmetic on elapsed time, use `datetimeUtc`.
   */
  datetime: string;
  /**
   * The same send as a true instant: ISO-8601 with a `Z` suffix. This is the
   * field for elapsed-time math — notably `smr-delivery-status`'s pending ->
   * "Sent" fall-through, whose SMS window is fifteen minutes, far narrower
   * than the error `datetime` would introduce.
   *
   * Optional so an older server (or a cached response) simply leaves the
   * staleness question unanswered rather than being answered wrongly.
   */
  datetimeUtc?: string | null;
  message: string;
  tracked: boolean;
  inProgress: boolean;
  totals: CampaignReportTotals | null;
  /** Text-only receipt header — what the salon was billed for this send. */
  recipientCount?: number | null;
  segmentCount?: number | null;
  totalCost?: number | null;
  /**
   * Text only: what the salon paid, net of refunds, in minor units. Null when
   * no charge was taken; absent from an older API.
   */
  costMinor?: number | null;
  /** Text only: the exact text recipients got. Absent from an older API. */
  sentMessage?: string | null;
}

export interface CampaignRecipientRow {
  clientID: number;
  name: string;
  destination: string;
  outcome: CampaignRecipientOutcome | null;
  /**
   * Real counts, NULL where the channel takes no such measurement — text has
   * no opens or clicks at all, and a 0 there would claim a measurement nobody
   * took (KTD-4).
   */
  opened: number | null;
  clicked: number | null;
  deliveredAt: string | null;
  lastActivity: string | null;
  /** Set when the client was merged away — the row links to the survivor. */
  mergedInto: number | null;
  /**
   * Whether this row can open a client record at all. A bulk send can name a
   * recipient the salon no longer has a client row for (deleted, or an address
   * that never was one), and that row must stay inert rather than link nowhere.
   */
  linkable: boolean;
}

/**
 * One count per filter the channel offers — the server sends only the keys its
 * channel supports, so a chip with no count simply doesn't render one.
 */
export type CampaignRecipientCounts = {
  [key in CampaignRecipientFilter]?: number;
};

/**
 * The recipients envelope exactly as the server sends it: `tracked`, `page`,
 * `rows` and `counts` together. `tracked` is false for a campaign that predates
 * delivery tracking (its rows carry no outcome) and null when the server said
 * nothing at all; `page` is the page the rows actually came from.
 */
export interface CampaignRecipientsPage {
  tracked: boolean | null;
  page: number | null;
  rows: CampaignRecipientRow[];
  counts: CampaignRecipientCounts | null;
}

export interface CampaignRecipientQuery {
  page?: number;
  filter?: CampaignRecipientFilter;
  sort?: CampaignRecipientSort;
  search?: string;
}

/** Recipients come back 30 to a page (KTD-14). */
export const CAMPAIGN_RECIPIENTS_PAGE_SIZE = 30;

/** The server rejects longer search terms; truncate rather than 400. */
const CAMPAIGN_SEARCH_MAX = 100;

@Injectable({
  providedIn: 'root'
})
export class SalonService extends SalonmonsterHttpClient {
  public taxes: List<Tax>;

  public salon: Salon;

  constructor(
    http: HttpClient,
    userService: UserService,
    protected errorHandlerService: ErrorHandlerService
  ) {
    super(http, userService, errorHandlerService);

    userService.getLogOutMonitor().subscribe((logout) => {
      if (logout) {
        this.taxes = null;
      }
    });
  }

  public async getIsReadOnly(): Promise<boolean> {
    if(this.salon) {
      return this.salon.isReadOnly();
    } 
    return false;
  }

  public getAllStaff(
    date: Date,
    options?: HttpReadOptions
  ): Observable<List<Stylist>> {
    const url = `${
      ENV_CONFIG.API_ROOT
    }/salons/allstaff/bookings?date=${Utilities.formatDate(date)}`;
    return this.get(url, options).pipe(
      map((data) => {
        let stylists: List<Stylist> = List([]);

        for (const stylist of data) {
          stylists = stylists.push(Stylist.parseStylistData(stylist));
        }

        return stylists;
      })
    );
  }

  public load(): Observable<Salon> {
    const url = `${ENV_CONFIG.API_ROOT}/salons`;
    return this.get(url).pipe(
      map((data) => {
        this.salon = Salon.parse(data[0]);
        return this.salon;
      })
    );
  }

  public getTaxes(forceLoad: boolean = false): Observable<List<Tax>> {
    if (this.taxes && !forceLoad) {
      return of(this.taxes);
    } else {
      const url = `${ENV_CONFIG.API_ROOT}/salons/taxes`;
      return this.get(url).pipe(
        map((data) => {
          let taxes: List<Tax> = List([]);

          for (const tax of data) {
            taxes = taxes.push(Tax.parse(tax));
          }

          return taxes;
        }),
        tap((taxes) => {
          this.taxes = taxes;
        })
      );
    }
  }

  public saveTax(tax: Tax): Observable<Tax> {
    let url = `${ENV_CONFIG.API_ROOT}/salons/taxes`;
    if (tax.id) {
      url += '/' + tax.id;

      return this.put(url, tax.serialize()).pipe(
        map((data) => {
          return Tax.parse(data);
        })
      );
    } else {
      return this.post(url, tax.serialize()).pipe(
        map((data) => {
          return Tax.parse(data);
        })
      );
    }
  }

  public deleteTax(tax: Tax): Observable<boolean> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/taxes/${tax.id}`;

    return this.delete(url, {}).pipe(
      map((data) => {
        return data.success;
      })
    );
  }

  public updateTaxes(taxes: List<Tax>) {
    this.taxes = taxes;
  }

  public getAllStaffAvailabilities(
    year: string,
    month: string,
    options?: HttpReadOptions
  ): Observable<Map<string, CalendarAvailabilities>> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/allstaff/availabilities?year=${year}&month=${month}`;
    return this.get(url, options).pipe(
      map((data) => {
        let allStaffAvailabilities: Map<string, CalendarAvailabilities> = Map();

        for (const stylistID in data) {
          if (Object.prototype.hasOwnProperty.call(data, stylistID)) {
            const element = data[stylistID];

            allStaffAvailabilities = allStaffAvailabilities.set(
              stylistID,
              Availability.parseAvailabilityData(element)
            );
          }
        }

        return allStaffAvailabilities;
      })
    );
  }

  public saveSalon(salon: Salon): Observable<Salon> {
    const url = `${ENV_CONFIG.API_ROOT}/salons`;
    return this.put(url, salon.serialize()).pipe(
      map((data) => {
        return Salon.parse(data);
      })
    );
  }

  /**
   * Saves whether online booking asks for a card on file, and optionally the
   * no-show percentage. Resolves with the values the server read back after
   * saving, which callers should apply rather than what they sent.
   */
  public saveCardOnFileSettings(
    settings: { requireCardOnFile: boolean; noShowPercentage?: number }
  ): Observable<CardOnFileSettings> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/card-on-file`;
    const body: typeof settings = {
      requireCardOnFile: settings.requireCardOnFile,
    };
    if (settings.noShowPercentage !== undefined) {
      body.noShowPercentage = settings.noShowPercentage;
    }
    return this.put<CardOnFileSettings>(url, body);
  }

  public getSalonPaymentTypes(): Observable<SalonPaymentTypes> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/paymenttypes`;
    return this.get(url).pipe(
      map((data) => {
        return SalonPaymentTypes.parse(data[0]);
      })
    );
  }

  public getSalonPaymentTypesObj(): Observable<any> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/paymenttypes`;
    return this.get(url).pipe(
      map((data) => {
        const paymentTypes =  SalonPaymentTypes.parse(data[0]);
        let paymentTypesObj = []
        if (paymentTypes.cash === 1) {
          paymentTypesObj.push(new Payment({ type: 1, name: 'Cash', amount: 0 }))
        }
        if (paymentTypes.debit === 1) {
          paymentTypesObj.push(new Payment({ type: 2, name: 'Debit', amount: 0 }))
        }
        if (paymentTypes.visa === 1) {
          paymentTypesObj.push(new Payment({ type: 3, name: 'Visa', amount: 0 }))
        }
        if (paymentTypes.mastercard === 1) {
          paymentTypesObj.push(new Payment({ type: 4, name: 'MasterCard', amount: 0 }))
        }
        if (paymentTypes.amex === 1) {
          paymentTypesObj.push(new Payment({ type: 5, name: 'AMEX', amount: 0 }))
        }
        if (paymentTypes.cheque === 1) {
          paymentTypesObj.push(new Payment({ type: 6, name: 'Check/Cheque', amount: 0 }))
        }
        if (paymentTypes.giftcard === 1) {
          paymentTypesObj.push(new Payment({ type: 7, name: 'Gift card', amount: 0 }))
        }
        if (paymentTypes.etransfer === 1) {
          paymentTypesObj.push(new Payment({ type: 8, name: 'e-Transfer', amount: 0 }))
        }
        if (paymentTypes.discover === 1) {
          paymentTypesObj.push(new Payment({ type: 9, name: 'Discover', amount: 0 }))
        }
        if (paymentTypes.account === 1) {
          paymentTypesObj.push(new Payment({ type: 10, name: 'Account', amount: 0 }))
        }
        if (paymentTypes.square === 1) {
          paymentTypesObj.push(new Payment({ type: 11, name: 'Square', amount: 0 }))
        }
        if (paymentTypes.shopify === 1) {
          paymentTypesObj.push(new Payment({ type: 30, name: 'Shopify', amount: 0 }))
        }
        if (paymentTypes.giftCardExternal === 1) {
          paymentTypesObj.push(new Payment({ type: 31, name: 'Giftcard(external)', amount: 0 }))
        }
        return paymentTypesObj;
      })
    );
  }

  public savePaymentType(
    salonPaymentTypes: SalonPaymentTypes
  ): Observable<SalonPaymentTypes> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/paymenttypes`;
    return this.put(url, salonPaymentTypes.serialize()).pipe(
      map((data) => {
        return SalonPaymentTypes.parse(data);
      })
    );
  }

  public getSalonDiscounts(): Observable<List<Discount>> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/discounts`;
    return this.get(url).pipe(
      map((data) => {
        let discounts: List<Discount> = List([]);

        for (const discountData of data) {
          discounts = discounts.push(Discount.parseDiscount(discountData));
        }

        return discounts;
      })
    );
  }

  public getClientsCount(): Observable<number> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/clients/count`;
    return this.get(url).pipe(
      map((data) => {
        return data.numOfClients;
      })
    );
  }

  public saveSalonDiscount(discount: Discount): Observable<Discount> {
    let url = `${ENV_CONFIG.API_ROOT}/salons/discounts`;

    if (discount.id) {
      url += '/' + discount.id;

      return this.put(url, discount.serialize()).pipe(
        map((data) => {
          return Discount.parseDiscount(data);
        })
      );
    } else {
      return this.post(url, discount.serialize()).pipe(
        map((data) => {
          return Discount.parseDiscount(data);
        })
      );
    }
  }

  public deleteSalonDiscounts(discount: Discount): Observable<Discount> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/discounts/${discount.id}`;
    return this.delete(url, {}).pipe(
      map((data) => {
        return Discount.parseDiscount(data);
      })
    );
  }

  /** The salon's bulk EMAIL campaign log. */
  public bulkCommunicationHistory(): Observable<List<Communication>> {
    return this.bulkCommunicationHistoryOfType('email');
  }

  /**
   * The salon's bulk TEXT campaign log — the same endpoint, keyed by type.
   *
   * Text campaigns carry no engagement axis: `uniqueOpened`, `uniqueClicked`
   * and `bounced` come back NULL, because SMS has no opens, clicks or bounces
   * and a 0 would claim a measurement nobody took (KTD-4). `sender` is empty.
   */
  public bulkTextCommunicationHistory(): Observable<List<Communication>> {
    return this.bulkCommunicationHistoryOfType('text');
  }

  private bulkCommunicationHistoryOfType(
    type: 'email' | 'text'
  ): Observable<List<Communication>> {
    const url =
      `${ENV_CONFIG.API_ROOT}/salons/bulkcommunications` +
      `?salonID=${this.userService.getSalon().getId()}&type=${type}`;

    return this.get(url).pipe(
      map((data) => {
        let communications: List<Communication> = List([]);

        for (const communicationData of data || []) {
          communications = communications.push(
            Communication.parse(communicationData)
          );
        }

        return communications;
      })
    );
  }

  /**
   * Campaign-level delivery aggregates. `totals` is NULL — never a row of
   * zeroes — for a campaign sent before delivery tracking existed (KTD-4);
   * callers must render the untracked sentence rather than 0%.
   *
   * A missing body is an error, not a report: handed to the success branch it
   * leaves the page rendering nothing at all — no retry, no empty state.
   */
  public getCampaignReport(
    campaignId: number,
    channel: CampaignChannel
  ): Observable<CampaignReport> {
    const url =
      `${ENV_CONFIG.API_ROOT}/salons/bulkcommunications/${channel}/${campaignId}/report` +
      `?salonID=${this.userService.getSalon().getId()}`;

    return this.get<CampaignReport>(url).pipe(
      map((report) => {
        if (!report) {
          throw new Error('Campaign report is unavailable');
        }
        return report;
      })
    );
  }

  /**
   * One 30-row page of a campaign's recipients. Filtering, sorting and search
   * are server-side (KTD-14) so the filter counts and the rows always agree.
   */
  public getCampaignRecipients(
    campaignId: number,
    channel: CampaignChannel,
    options: CampaignRecipientQuery
  ): Observable<CampaignRecipientsPage> {
    const page = options.page && options.page > 0 ? options.page : 1;
    const filter = options.filter || 'all';
    const sort = options.sort || 'problems';
    const search = (options.search || '').trim().slice(0, CAMPAIGN_SEARCH_MAX);

    let url =
      `${ENV_CONFIG.API_ROOT}/salons/bulkcommunications/${channel}/${campaignId}/report/recipients` +
      `?salonID=${this.userService.getSalon().getId()}` +
      `&page=${page}&filter=${filter}&sort=${sort}`;

    if (search) {
      url = `${url}&search=${encodeURIComponent(search)}`;
    }

    return this.get<CampaignRecipientsPage>(url).pipe(
      map((data) => ({
        tracked: data && data.tracked != null ? !!data.tracked : null,
        page: (data && data.page) || null,
        rows: (data && data.rows) || [],
        counts: (data && data.counts) || null,
      }))
    );
  }

  public subDomainChecker(subDomain: string): Observable<boolean> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/subdomainchecker/${subDomain}`;

    return this.get(url).pipe(
      map((data) => {
        return data.available;
      })
    );
  }

  public getOnlineBookingSharePhotos(): Observable<List<string>> {
    const url = `${ENV_CONFIG.API_ROOT}/onlinebooking/share`;

    return this.get(url).pipe(
      map((data) => {
        let photos: List<string> = List([]);
        for (const photo of data) {
          photos = photos.push(photo);
        }

        return photos;
      })
    );
  }

  public deleteSalon(salonID: string, salonName: string): Observable<any> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/${salonID}/deletesalon`;
    const requestBody = {
      salonID: salonID,
      salonName: salonName,
    };

    return this.delete(url, requestBody).pipe(
      map((data) => {
        return data;
      })
    );
  }
}
